import axios from "axios";
import appConfig from "../config/appConfig";
import AuthHelper from "../components/auth/AuthLib";

const SLSApiAxiosInstance = (function () {
  let instance;

  function createInstance() {
    const config = {
      headers: {
        "Content-Type": "application/vnd.coxauto.v2+json",
        Accept: "application/json"
      },
      // Auth is bearer-token only (Authorization header set in refreshAuthHeader);
      // no cookies are ever exchanged, so this flag is functionally a no-op against
      // the current HTTP API gateway. Required for the REST API cutover: that
      // gateway's CORS does not emit Access-Control-Allow-Credentials, so true
      // would cause the browser to reject every preflight.
      withCredentials: false,
      baseURL: appConfig.bff.baseURL
    };
    return axios.create(config);
  }

  return {
    getInstance: () => {
      if (!instance) {
        instance = createInstance();
      }
      return instance;
    }
  };
})();

async function refreshAuthHeader() {
  const token = await AuthHelper.getToken();
  SLSApiAxiosInstance.getInstance().defaults.headers["Authorization"] =
    "Bearer " + token;
}

export async function createSolutionLinkFromAuthCode(
  solutionId,
  redirectUrl,
  authorizationCode
) {
  await refreshAuthHeader();
  return SLSApiAxiosInstance.getInstance().post("/sls/create-link", {
    solutionId,
    redirectUrl,
    authorizationCode
  });
}

/**
 * Fetches account merge preview data using the OAuth auth code.
 * The code can be reused for the actual merge operation.
 * @param {string} authorizationCode - The JWE auth code from SignIn /bridge-identity flow
 * @param {string} redirectUri - The redirect URI used in the OAuth flow
 * @param {boolean} swapAccounts - If true, preview with swapped accounts (other user becomes surviving)
 * @returns {Promise} - Preview data including currentUser, otherUser, and combined result
 */
export async function getAccountMergePreview(
  authorizationCode,
  redirectUri,
  swapAccounts = false
) {
  await refreshAuthHeader();
  return SLSApiAxiosInstance.getInstance().post("/account-merge/preview", {
    authorizationCode,
    redirectUri,
    swapAccounts
  });
}

/**
 * Executes the account merge operation.
 * @param {string} authorizationCode - The JWE auth code (same one used for preview)
 * @param {string} redirectUri - The redirect URI used in the OAuth flow
 * @param {boolean} swapAccounts - If true, swap which account is surviving vs non-surviving
 * @returns {Promise} - Merge result
 */
export async function executeAccountMerge(
  authorizationCode,
  redirectUri,
  swapAccounts = false
) {
  await refreshAuthHeader();
  return SLSApiAxiosInstance.getInstance().post("/account-merge/merge", {
    authorizationCode,
    redirectUri,
    swapAccounts
  });
}

/**
 * Verifies that the account merge has fully propagated in the backend.
 * @param {string} authorizationCode - The JWE auth code used for the merge
 * @param {boolean} swapAccounts - Whether accounts were swapped during merge
 * @param {Object} expected - The expected merge result (e.g. { add: { solutionLinks: [...] } })
 * @returns {Promise} - Verification result with { inSync: boolean }
 */
export async function verifyMergeStatus(
  authorizationCode,
  swapAccounts,
  expected
) {
  await refreshAuthHeader();
  return SLSApiAxiosInstance.getInstance().post(
    "/account-merge/verify-status",
    {
      authorizationCode,
      swapAccounts,
      expected
    },
    { timeout: 5000 } // Cap individual request to 5s so a hung backend can't stall the entire retry budget
  );
}
